import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { subscriptionsAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  CalendarBlank, ArrowLeft, Check, Clock, X, Wallet, CreditCard,
  CaretRight, Warning, User
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

const STATUS_CONFIG = {
  active: { label: 'Active', color: '#22C55E', bg: 'bg-[#22C55E]/10 text-[#22C55E]' },
  pending: { label: 'Pending', color: '#F59E0B', bg: 'bg-[#F59E0B]/10 text-[#F59E0B]' },
  past_due: { label: 'Past Due', color: '#EF4444', bg: 'bg-[#EF4444]/10 text-[#EF4444]' },
  cancelled: { label: 'Cancelled', color: '#666666', bg: 'bg-[#333333] text-[#999999]' },
};

export default function MySubscriptionsPage() {
  const [mySubs, setMySubs] = useState([]);
  const [mySubscribers, setMySubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('subscriptions');

  const loadData = useCallback(async () => {
    try {
      const [subsRes, subscribersRes] = await Promise.all([
        subscriptionsAPI.getMy(),
        subscriptionsAPI.getSubscribers()
      ]);
      setMySubs(subsRes.data.subscriptions || []);
      setMySubscribers(subscribersRes.data.subscribers || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCancel = async (subId, productName, monthsPaid, minMonths) => {
    if (monthsPaid < minMonths) {
      toast.error(`Cannot cancel yet. Minimum commitment: ${minMonths} months. You've paid ${monthsPaid} months.`);
      return;
    }
    if (!window.confirm(`Cancel your subscription to ${productName}?`)) return;
    try {
      await subscriptionsAPI.cancel(subId);
      toast.success('Subscription cancelled');
      loadData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const activeCount = mySubs.filter(s => s.status === 'active').length;
  const totalSpent = mySubs.reduce((sum, s) => sum + (s.total_paid || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="animate-pulse text-[#999999]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111]">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
          <Link to="/dashboard">
            <Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" />Dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white flex items-center gap-3" style={{ fontFamily: 'Cabinet Grotesk' }}>
            <CalendarBlank className="w-7 h-7 text-[#22C55E]" weight="fill" />
            Subscriptions
          </h1>
          <p className="text-[#999999] mt-1">Manage your subscriptions and view your subscribers</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid="stat-active">
            <CardContent className="p-5 text-center">
              <p className="text-2xl font-bold text-[#22C55E]">{activeCount}</p>
              <p className="text-xs text-[#999999] mt-1">Active</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid="stat-total-spent">
            <CardContent className="p-5 text-center">
              <p className="text-2xl font-bold text-white">{formatCurrency(totalSpent)}</p>
              <p className="text-xs text-[#999999] mt-1">Total Spent</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid="stat-subscribers">
            <CardContent className="p-5 text-center">
              <p className="text-2xl font-bold text-[#22C55E]">{mySubscribers.length}</p>
              <p className="text-xs text-[#999999] mt-1">My Subscribers</p>
            </CardContent>
          </Card>
        </div>

        {/* Tab Toggle */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setTab('subscriptions')}
            variant={tab === 'subscriptions' ? 'default' : 'outline'}
            className={`rounded-sm ${tab === 'subscriptions' ? 'bg-[#22C55E] hover:bg-[#16A34A] text-white' : 'border-[#333333] text-[#999999] hover:text-white hover:bg-[#333333]'}`}
            data-testid="tab-my-subscriptions"
          >
            <CalendarBlank className="w-4 h-4 mr-2" />
            My Subscriptions ({mySubs.length})
          </Button>
          <Button
            onClick={() => setTab('subscribers')}
            variant={tab === 'subscribers' ? 'default' : 'outline'}
            className={`rounded-sm ${tab === 'subscribers' ? 'bg-[#22C55E] hover:bg-[#16A34A] text-white' : 'border-[#333333] text-[#999999] hover:text-white hover:bg-[#333333]'}`}
            data-testid="tab-my-subscribers"
          >
            <User className="w-4 h-4 mr-2" />
            My Subscribers ({mySubscribers.length})
          </Button>
        </div>

        {/* My Subscriptions Tab */}
        {tab === 'subscriptions' && (
          <div className="space-y-4" data-testid="subscriptions-list">
            {mySubs.length === 0 ? (
              <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none">
                <CardContent className="p-8 text-center">
                  <CalendarBlank className="w-12 h-12 mx-auto mb-3 text-[#333333]" />
                  <p className="text-[#999999]">No subscriptions yet</p>
                  <Link to="/explore"><Button size="sm" className="mt-3 bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">Browse Services</Button></Link>
                </CardContent>
              </Card>
            ) : (
              mySubs.map((sub) => {
                const statusConf = STATUS_CONFIG[sub.status] || STATUS_CONFIG.pending;
                const progress = sub.min_commitment_months ? Math.min(100, (sub.months_paid / sub.min_commitment_months) * 100) : 0;
                const canCancel = sub.status === 'active' && sub.months_paid >= sub.min_commitment_months;

                return (
                  <Card key={sub.subscription_id} className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none overflow-hidden" data-testid={`sub-${sub.subscription_id}`}>
                    <CardContent className="p-0">
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#22C55E]/10">
                              <CalendarBlank className="w-5 h-5 text-[#22C55E]" weight="fill" />
                            </div>
                            <div>
                              <Link to={`/product/${sub.product_id}`} className="font-bold text-white text-sm hover:text-[#22C55E] transition-colors">
                                {sub.product_name} <CaretRight className="w-3 h-3 inline" />
                              </Link>
                              <p className="text-xs text-[#999999]">by {sub.seller?.name || 'Seller'} {sub.seller?.username ? `($${sub.seller.username})` : ''}</p>
                            </div>
                          </div>
                          <span className={`text-[10px] font-bold px-2 py-1 rounded ${statusConf.bg}`}>{statusConf.label}</span>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                          <div className="bg-[#111111] rounded-sm p-3">
                            <p className="text-[10px] uppercase tracking-wider text-[#666666] font-semibold">Price</p>
                            <p className="text-sm font-bold text-[#22C55E]">{formatCurrency(sub.price)}/mo</p>
                          </div>
                          <div className="bg-[#111111] rounded-sm p-3">
                            <p className="text-[10px] uppercase tracking-wider text-[#666666] font-semibold">Paid</p>
                            <p className="text-sm font-bold text-white">{sub.months_paid} month{sub.months_paid !== 1 ? 's' : ''}</p>
                          </div>
                          <div className="bg-[#111111] rounded-sm p-3">
                            <p className="text-[10px] uppercase tracking-wider text-[#666666] font-semibold">Total Spent</p>
                            <p className="text-sm font-bold text-white">{formatCurrency(sub.total_paid)}</p>
                          </div>
                          <div className="bg-[#111111] rounded-sm p-3">
                            <p className="text-[10px] uppercase tracking-wider text-[#666666] font-semibold">Payment</p>
                            <p className="text-sm font-bold text-white flex items-center gap-1">
                              {sub.payment_method === 'wallet' ? <Wallet className="w-3.5 h-3.5" /> : <CreditCard className="w-3.5 h-3.5" />}
                              {sub.payment_method === 'wallet' ? 'Wallet' : 'Card'}
                            </p>
                          </div>
                        </div>

                        {/* Commitment Progress */}
                        {sub.min_commitment_months && (
                          <div className="mb-4">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-[#999999]">Commitment: {sub.months_paid}/{sub.min_commitment_months} months</span>
                              {sub.months_paid >= sub.min_commitment_months ? (
                                <span className="text-[#22C55E] flex items-center gap-1"><Check className="w-3 h-3" /> Complete</span>
                              ) : (
                                <span className="text-[#F59E0B] flex items-center gap-1"><Clock className="w-3 h-3" /> {sub.min_commitment_months - sub.months_paid} left</span>
                              )}
                            </div>
                            <div className="w-full h-1.5 bg-[#333333] rounded-full overflow-hidden">
                              <div className="h-full bg-[#22C55E] rounded-full transition-all" style={{ width: `${progress}%` }} />
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <div className="text-xs text-[#666666]">
                            {sub.status === 'active' && sub.next_billing_date && (
                              <span>Next billing: {formatDate(sub.next_billing_date)}</span>
                            )}
                            {sub.status === 'past_due' && (
                              <span className="text-[#EF4444] flex items-center gap-1"><Warning className="w-3 h-3" /> Payment failed — add funds to resume</span>
                            )}
                            {sub.status === 'cancelled' && (
                              <span>Cancelled {formatDate(sub.cancelled_at)}</span>
                            )}
                          </div>
                          {sub.status === 'active' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCancel(sub.subscription_id, sub.product_name, sub.months_paid, sub.min_commitment_months)}
                              className={`rounded-sm text-xs ${canCancel ? 'text-red-400 hover:text-red-300 hover:bg-red-400/10' : 'text-[#666666] hover:text-[#999999]'}`}
                              data-testid={`cancel-sub-${sub.subscription_id}`}
                            >
                              <X className="w-3 h-3 mr-1" />
                              {canCancel ? 'Cancel' : `Cancel (after ${sub.min_commitment_months}mo)`}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* My Subscribers Tab */}
        {tab === 'subscribers' && (
          <div className="space-y-4" data-testid="subscribers-list">
            {mySubscribers.length === 0 ? (
              <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none">
                <CardContent className="p-8 text-center">
                  <User className="w-12 h-12 mx-auto mb-3 text-[#333333]" />
                  <p className="text-[#999999]">No subscribers yet</p>
                  <p className="text-xs text-[#666666] mt-1">Create a subscription product to start getting subscribers</p>
                </CardContent>
              </Card>
            ) : (
              mySubscribers.map((sub) => (
                <Card key={sub.subscription_id} className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid={`subscriber-${sub.subscription_id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#22C55E]/10 flex items-center justify-center">
                          <span className="text-[#22C55E] font-bold text-sm">{sub.subscriber_name?.[0] || '?'}</span>
                        </div>
                        <div>
                          <p className="font-bold text-white text-sm">{sub.subscriber_name}</p>
                          <p className="text-xs text-[#999999]">{sub.subscriber_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-[#22C55E]">{formatCurrency(sub.price)}/mo</p>
                        <p className="text-xs text-[#999999]">{sub.product_name}</p>
                        <p className="text-[10px] text-[#666666]">{sub.months_paid} month{sub.months_paid !== 1 ? 's' : ''} paid</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
